'use client';

import { FC, ReactNode, useEffect } from 'react';
import { X } from 'lucide-react';
import styles from './Modal.module.scss';
interface ModalProps {
  show: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  size?: 'md' | 'sm';
  showClose?: boolean;
  /**
   * Lets descendants escape the card's bounding box (e.g. the
   * react-intl-tel-input country popup in EditPhoneModal). Drops the
   * card's clipped scrolling, so callers must keep their own content
   * short enough to fit.
   */
  allowOverflow?: boolean;
}
const Modal: FC<ModalProps> = ({
  show,
  onClose,
  title,
  children,
  size = 'md',
  showClose = true,
  allowOverflow = false
}) => {
  useEffect(() => {
    if (!show) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [show, onClose]);
  if (!show) return null;
  return <div className={styles.backdrop} onClick={e => {
    if (e.target === e.currentTarget) onClose();
  }} data-sentry-component="Modal" data-sentry-source-file="Modal.tsx">
            <div className={`${styles.card} ${size === 'sm' ? styles.cardSm : ''} ${allowOverflow ? styles.cardOverflowVisible : ''}`.trim()} role="dialog" aria-modal="true" aria-label={title}>
                {(title || showClose) && <div className={styles.header}>
                        {title ? <h3 className={styles.title}>{title}</h3> : <span />}
                        {showClose && <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
                                <X size={16} strokeWidth={2} />
                            </button>}
                    </div>}
                {children}
            </div>
        </div>;
};
export default Modal;